import React, { useEffect, useState, useCallback } from "react";
import { rtdb } from "../firebase";
import { ref, get } from "firebase/database"; 
import "./AdminWorkstation.css";

function AdminWorkstation({ onBack }) {
  const [workstations, setWorkstations] = useState({
    Receive: [], Treat: [], Paint: [], Pack: [], Deliver: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Track only the identifier key of the selected workstation instead of storing stale copies
  const [selectedWSKey, setSelectedWSKey] = useState(null);

  const fetchWorkstationStatus = useCallback(async (isMounted) => {
    setLoading(true);
    const dbRef = ref(rtdb, "Workstation");
    try {
      const snapshot = await get(dbRef);
      if (snapshot.exists() && isMounted) {
        const data = snapshot.val();
        const updatedWS = { Receive: [], Treat: [], Paint: [], Pack: [], Deliver: [] };
        
        Object.values(data).forEach((staffEntry) => {
          const { place, name } = staffEntry;
          if (place && name && updatedWS[place]) {
            updatedWS[place].push(name);
          }
        });
        setWorkstations(updatedWS);
      }
    } catch (error) {
      console.error("Error fetching Workstation node:", error);
    } finally {
      if (isMounted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchWorkstationStatus(isMounted);
    return () => { isMounted = false; };
  }, [refreshKey, fetchWorkstationStatus]);

  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  // Meta mapping for titles and icons associated with internal database node keys
  const workstationMeta = {
    Receive: { title: "Receiving", icon: "📥" },
    Treat: { title: "Treatment", icon: "💦" },
    Paint: { title: "Painting", icon: "🎨" },
    Pack: { title: "Packing", icon: "📦" },
    Deliver: { title: "Delivery", icon: "🚚" },
  };

  const renderWS = (place) => {
    const { title, icon } = workstationMeta[place];
    const staffList = workstations[place] || [];
    const isActive = staffList.length > 0;

    return (
      <div 
        className={`aws-glass-card ${isActive ? "aws-active-ws" : ""}`} 
        onClick={() => setSelectedWSKey(place)}
      >
        <div className="aws-status-indicator">
          <span className={`aws-dot ${isActive ? "aws-pulse" : ""}`}></span>
          {isActive ? "LIVE" : "IDLE"}
        </div>
        <div className="aws-ws-icon">{icon}</div>
        <h3 className="aws-ws-label">{title}</h3>
        <div className="aws-ws-staff-count">
          {loading ? "..." : `${staffList.length} Staff`}
        </div>
        <div className="aws-ws-staff-preview">
          {isActive 
            ? `${staffList.slice(0, 2).join(", ")}${staffList.length > 2 ? "..." : ""}` 
            : "No active staff"}
        </div>
      </div>
    );
  };

  // Derive dynamic details for the open modal directly from state to prevent holding stale cache records
  const selectedWSDetails = selectedWSKey ? {
    place: selectedWSKey,
    ...workstationMeta[selectedWSKey],
    staffList: workstations[selectedWSKey] || [],
    isActive: (workstations[selectedWSKey] || []).length > 0
  } : null;

  return (
    <div className="aws-admin-page">
      <div className="aws-overlay">
        <header className="aws-glass-header">
          <div className="aws-header-content">
            <div className="aws-header-title-wrapper">
              <h2>Staff Workstation</h2>
            </div>
            
            <button className="aws-modern-refresh-btn" onClick={handleRefresh} disabled={loading}>
              {loading ? <span className="aws-spinner"></span> : "Refresh"}
            </button>
          </div>
        </header>

        <main className="aws-flow-container">
          <div className="aws-grid-modern">
            {renderWS("Receive")}
            <div className="aws-flow-arrow">➜</div>
            {renderWS("Treat")}
            <div className="aws-flow-arrow">➜</div>
            {renderWS("Paint")}
            <div className="aws-flow-arrow">➜</div>
            {renderWS("Pack")}
            <div className="aws-flow-arrow">➜</div>
            {renderWS("Deliver")}
          </div>
        </main>
      </div>

      {/* --- MODERN GLASS MODAL --- */}
      {selectedWSDetails && (
        <div className="aws-modal-backdrop" onClick={() => setSelectedWSKey(null)}>
          <div className="aws-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="aws-modal-header">
              <div className="aws-modal-title-group">
                <span className="aws-modal-icon-large">{selectedWSDetails.icon}</span>
                <div>
                  <h3>{selectedWSDetails.title}</h3>
                  <p>{selectedWSDetails.isActive ? "Active" : "Station Offline"}</p>
                </div>
              </div>
              <button className="aws-close-btn" onClick={() => setSelectedWSKey(null)}>×</button>
            </div>
            
            <div className="aws-modal-body">
              {selectedWSDetails.isActive ? (
                <div className="aws-staff-list-container">
                  {selectedWSDetails.staffList.map((name, index) => (
                    <div key={index} className="aws-staff-card-item">
                      <div className="aws-staff-avatar">{name.charAt(0)}</div>
                      <span className="aws-staff-name">{name}</span>
                      <div className="aws-online-tag">Online</div>
                    </div>
                  ))}
                </div>
              ) : (
                /* NEW EMPTY STATE VIEW */
                <div className="aws-empty-state-container">
                  <div className="aws-empty-state-icon">😴</div>
                  <p>Currently, no staff members are clocked into this workstation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminWorkstation;